import React, { useState, useEffect, useContext } from "react";
import Logger from "../../util/logger";
import * as Papa from "papaparse";
import Label from "../common/label";
import { string } from "prop-types";

const logger = Logger("session-provider");

interface SessionContextValue {
  config: Config | null;
  banklist: BankData[] | null;
  countrylist: CountryData[] | null;
  institutionlist: InstitutionData[] | null;
  countriesEN: { value: string; label: string; }[] | null;
  countriesFR: { value: string; label: string; }[] | null;
  currency: { value: string; label: string; }[] | null;
}
export interface Config {
  API_URL: string;
  REGION: string;
  POOL_ID: string;
  CLIENT_ID: string;
  APP_TZ: string;
  SESSION_TIMEOUT: number;
  CAN_POST_API_KEY: string;
}

export interface BankData {
  country_EN: string;
  country_FR: string;
  countryCode: string;
  sortCode_min: number;
  sortCode_max: number;
  sortCord_req: string;
  swiftBic_min: number;
  swiftBic_max: number;
  swiftBic_req: string;
  accntOrIBAN_min: number;
  accntOrIBAN_max: number;
  accntOrIBAN_req: string;
}
export interface CountryData {
  country_Code: string;
  country_EN: string;
  country_FR: string;
}
export interface InstitutionData {
  institutionNo: string;
  name_EN: string;
  name_FR: string;
}

export interface CurrencyData {
  currency_code: string;
}
export const SessionContext = React.createContext({} as SessionContextValue);

/**
 * Root session context
 */
export const SessionProvider: React.FC = (props) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [banklist, setBanklist] = useState<BankData[] | null>(null);
  const [countrylist, setCountrylist] = useState<CountryData[] | null>(null);
  const [countriesEN, setCountriesEN] = useState<{ value: string; label: string; }[] | null>(null);
  const [countriesFR, setCountriesFR] = useState<{ value: string; label: string; }[] | null>(null);
  const [institutionlist, setInstitutionList] = useState<InstitutionData[] | null>(null);
  const [currencylist, setCurrencylist] = useState<CurrencyData[] | null>(null)
  const [currency, setCurrency] = useState<{ value: string; label: string; }[] | null>(null);

  useEffect(() => {
    fetch(`/config.json`, {})
      .then((res) => res.json())
      .then((config) => {
        logger.info("config fetched", config);
        setConfig(config);
      })
      .catch((err) => {
        logger.error("fetch config error", err);
      });
  }, []);

  useEffect(() => {
    fetch(`/banklist.csv`, {})
      .then((res) => res.text())
      .then((csv) => Papa.parse(csv, {
        delimiter: ",",
        header: true,
        dynamicTyping: false
      }))
      .then((banklist) => {
        logger.info("banklist fetched", banklist);
        setBanklist(banklist.data as BankData[]);
      })
      .catch((err) => {
        logger.error("fetch banklist error", err);
      });
  }, []);

  useEffect(() => {
    fetch(`/institutionlist.csv`, {})
      .then((res) => res.text())
      .then((csv) => Papa.parse(csv, {
        delimiter: ",",
        header: true,
        dynamicTyping: false
      }))
      .then((institutionlist) => {
        logger.info("institutionlist fetched", institutionlist);
        setInstitutionList(institutionlist.data as InstitutionData[]);
      })
      .catch((err) => {
        logger.error("fetch institutionlist error", err);
      });
  }, []);

  useEffect(() => {
    fetch(`/country.csv`, {})
      .then((res) => res.text())
      .then((csv) => Papa.parse(csv, {
        delimiter: ",",
        header: true,
        dynamicTyping: false
      }))
      .then((countrydata) => {
        logger.info("countrylist fetched", countrydata);
        setCountrylist(countrydata.data as CountryData[]);
      })
      .catch((err) => {
        logger.error("fetch banklist error", err);
      });
  }, []);

  useEffect(() => {
    fetch(`/currency.csv`, {})
      .then((res) => res.text())
      .then((csv) => Papa.parse(csv, {
        delimiter: ",",
        header: true,
        dynamicTyping: false
      }))
      .then((currencylist) => {
        logger.info("currencylist fetched", currencylist);
        setCurrencylist(currencylist.data as CurrencyData[]);
      })
      .catch((err) => {
        logger.error("fetch currencylist error", err);
      });
  }, []);

  useEffect(() => {
    if (countrylist) {
      const countries_EN = new Array<{ value: string; label: string; }>();
      const countries_FR = new Array<{ value: string; label: string; }>();

      countrylist.forEach(x => {
        countries_EN.push({ value: x.country_Code, label: x.country_EN });
        countries_FR.push({ value: x.country_Code, label: x.country_FR });
      }); 
      countries_FR.sort((a, b) => {
        if (a.value === 'CA') {
          return -1; 
        } else if (b.value === 'CA') {
          return 1;
        } else {
          return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
        }
      });
      
      setCountriesEN(countries_EN);
      setCountriesFR(countries_FR);
    }
  }, [countrylist])

  useEffect(() => {
    if (currencylist) {
      const currency = new Array<{ value: string; label: string; }>();

      currencylist.forEach(x => {
        currency.push({ value: x.currency_code, label: x.currency_code })
      });
      setCurrency(currency);
    }
  }, [currencylist])

  return (
    <SessionContext.Provider
      value={{
        config,
        banklist,
        institutionlist,
        countrylist,
        countriesEN,
        countriesFR,
        currency
      }}
    >
      {props.children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
